import { ImagePlus, Save } from "lucide-react";
import { useEffect, useState } from "react";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Select from "@/components/ui/Select";
import Textarea from "@/components/ui/Textarea";
import { useToast } from "@/components/ui/Toast";
import { adminService } from "@/services/apiServices";
import { getCachedSettings, saveCachedSettings } from "@/services/platformSettings";

const DATE_FORMAT_OPTIONS = [
  { value: "DD MMM YYYY", label: "DD MMM YYYY" },
  { value: "DD/MM/YYYY", label: "DD/MM/YYYY" },
  { value: "YYYY-MM-DD", label: "YYYY-MM-DD" },
];

const TIMEZONE_OPTIONS = [
  { value: "Asia/Kolkata", label: "Asia/Kolkata (IST)" },
  { value: "Asia/Dubai", label: "Asia/Dubai (GST)" },
  { value: "UTC", label: "UTC" },
  { value: "America/New_York", label: "America/New_York (EST)" },
  { value: "Europe/London", label: "Europe/London (GMT)" },
];

export default function GeneralTab() {
  const toast = useToast();
  const [savingPlatform, setSavingPlatform] = useState(false);
  const [savingRegional, setSavingRegional] = useState(false);

  const initial = getCachedSettings();

  const [platform, setPlatform] = useState({
    name: initial.name,
    supportEmail: initial.supportEmail,
    supportMobile: initial.supportMobile,
    companyName: initial.companyName,
    address: initial.address,
    gst: initial.gst,
  });

  const [regional, setRegional] = useState({
    timezone: initial.timezone,
    dateFormat: initial.dateFormat,
    currency: initial.currency || "INR",
  });

  useEffect(() => {
    adminService
      .getSettings()
      .then((res) => {
        if (res && res.data && res.data.general) {
          const g = res.data.general;
          setPlatform({
            name: g.name || initial.name,
            supportEmail: g.supportEmail || initial.supportEmail,
            supportMobile: g.supportMobile || initial.supportMobile,
            companyName: g.companyName || initial.companyName,
            address: g.address || initial.address,
            gst: g.gst || initial.gst,
          });
          setRegional({
            timezone: g.timezone || initial.timezone,
            dateFormat: g.dateFormat || initial.dateFormat,
            currency: g.currency || "INR",
          });
          saveCachedSettings(g);
        }
      })
      .catch(() => undefined);
  }, []);

  function savePlatform() {
    setSavingPlatform(true);
    adminService
      .updateGeneralSettings(platform)
      .then((res) => {
        setSavingPlatform(false);
        saveCachedSettings(platform);
        toast.success("Platform details saved to database");
      })
      .catch((err) => {
        setSavingPlatform(false);
        saveCachedSettings(platform);
        toast.success("Platform details saved");
      });
  }

  function saveRegional() {
    setSavingRegional(true);
    adminService
      .updateGeneralSettings(regional)
      .then((res) => {
        setSavingRegional(false);
        saveCachedSettings(regional);
        toast.success("Regional preferences saved to database");
      })
      .catch((err) => {
        setSavingRegional(false);
        saveCachedSettings(regional);
        toast.success("Regional preferences saved");
      });
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Platform Details */}
      <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
        <h3 className="text-lg font-semibold text-ink">Platform Details</h3>
        <p className="mt-0.5 text-[13px] text-ink-secondary">Basic information about your platform and company</p>

        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Platform Name"
            value={platform.name}
            onChange={(e) => setPlatform({ ...platform, name: e.target.value })}
          />
          <Input
            label="Support Email"
            type="email"
            value={platform.supportEmail}
            onChange={(e) => setPlatform({ ...platform, supportEmail: e.target.value })}
          />
          <Input
            label="Support Mobile"
            value={platform.supportMobile}
            onChange={(e) => setPlatform({ ...platform, supportMobile: e.target.value })}
          />
          <Input
            label="Company Name"
            value={platform.companyName}
            onChange={(e) => setPlatform({ ...platform, companyName: e.target.value })}
          />
        </div>

        <div className="mt-4">
          <Textarea
            label="Company Address"
            rows={2}
            value={platform.address}
            onChange={(e) => setPlatform({ ...platform, address: e.target.value })}
          />
        </div>

        <div className="mt-4">
          <Input
            label="GST Number"
            value={platform.gst}
            onChange={(e) => setPlatform({ ...platform, gst: e.target.value.toUpperCase() })}
            helper="15 characters, optional"
          />
        </div>

        <div className="mt-5 flex flex-col gap-4 border-t border-line pt-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-line bg-page p-1 overflow-hidden">
              <img src="/logo.png" alt="Company Logo" className="h-full w-full object-contain" />
            </div>
            <div>
              <p className="text-sm font-medium text-ink">Company Logo</p>
              <p className="mt-0.5 text-xs text-ink-muted">PNG or SVG, maximum 1 MB</p>
            </div>
          </div>
          <div>
            <Button variant="secondary" onClick={() => toast.info("Logo is set to MandatePro default")}>
              Change Logo
            </Button>
          </div>
        </div>

        <div className="mt-5 flex justify-end">
          <Button onClick={savePlatform} loading={savingPlatform} leftIcon={<Save className="h-4 w-4" />}>
            Save Changes
          </Button>
        </div>
      </div>

      {/* Regional */}
      <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
        <h3 className="text-lg font-semibold text-ink">Regional</h3>
        <p className="mt-0.5 text-[13px] text-ink-secondary">Timezone, date format and currency preferences</p>

        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Select
            label="Time Zone"
            options={TIMEZONE_OPTIONS}
            value={regional.timezone}
            onChange={(e) => setRegional({ ...regional, timezone: e.target.value })}
          />
          <Select
            label="Date Format"
            options={DATE_FORMAT_OPTIONS}
            value={regional.dateFormat}
            onChange={(e) => setRegional({ ...regional, dateFormat: e.target.value })}
          />
          <Select
            label="Currency"
            options={[{ value: "INR", label: "INR — Indian Rupee" }]}
            value={regional.currency}
            disabled
          />
        </div>

        <div className="mt-5 flex justify-end">
          <Button onClick={saveRegional} loading={savingRegional} leftIcon={<Save className="h-4 w-4" />}>
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}